/**
 * StoryBody — renders an author-written plain-text story body, turning pasted
 * URLs into safe links and sole-line YouTube URLs into embedded players.
 *
 * Why this is safe: the body is stored as PLAIN TEXT and React escapes every
 * text node. We never use dangerouslySetInnerHTML and never render
 * author-supplied HTML/iframes. Links and the embed iframe are built here from
 * our own templates using a validated href / video id (see lib/storyText.js),
 * so there is no injection surface — a hostile string just renders as text.
 *
 * Block model (lib/storyText.parseStoryBlocks):
 *   - a line that is ONLY a recognised YouTube URL → embedded player
 *   - everything else → a paragraph that preserves newlines (whitespace-pre-wrap)
 *     with inline http(s) URLs linkified.
 */
import { parseStoryBlocks, tokenizeInline, youTubeEmbedSrc } from '@/lib/storyText';

const renderInline = (value) =>
  tokenizeInline(value).map((tok, i) =>
    tok.type === 'link' ? (
      <a
        key={i}
        href={tok.value}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="text-[#C05621] underline underline-offset-2 hover:text-[#A0461A] break-words"
      >
        {tok.value}
      </a>
    ) : (
      // Plain string child — React escapes it. No key needed for strings.
      tok.value
    ),
  );

const StoryBody = ({ text, className = 'text-base md:text-lg leading-loose whitespace-pre-wrap' }) => {
  const blocks = parseStoryBlocks(text);

  return blocks.map((block, i) =>
    block.type === 'embed' ? (
      <div
        key={i}
        className="my-6 aspect-video w-full overflow-hidden rounded-lg border border-[#E6E2D6] bg-black"
        data-testid="story-youtube-embed"
      >
        <iframe
          className="h-full w-full"
          src={youTubeEmbedSrc(block)}
          title="Embedded YouTube video"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    ) : (
      <p key={i} className={className}>
        {renderInline(block.value)}
      </p>
    ),
  );
};

export default StoryBody;
